import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Select,
  Divider,
  Switch,
  Upload,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  UserOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

interface RoleRecord {
  id: number;
  name: string;
  alias: string;
  gender: 'female' | 'male';
  age: number;
  personality: string;
  occupation: string;
  tags: string[];
  intro: string;
  background: string;
  avatar: string;
  images: string[];
  videos: string[];
  scriptCount: number;
  storyCount: number;
  sceneCount: number;
  status: 'active' | 'inactive';
  updatedAt: string;
}

const initialRoles: RoleRecord[] = [
  {
    id: 1,
    name: '夜港歌姬',
    alias: '陈映秋',
    gender: 'female',
    age: 26,
    personality: '清冷、敏锐、善于隐藏情绪',
    occupation: '驻唱歌手',
    tags: ['港风', '悬疑', '夜色'],
    intro: '港城夜色里的歌者，带你走进一段悬疑旧案。',
    background: '她在港口长大，目睹旧案发生后始终未能释怀。',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200',
    images: [
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800',
      'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800',
    ],
    videos: [
      'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4',
    ],
    scriptCount: 1,
    storyCount: 50,
    sceneCount: 50,
    status: 'active',
    updatedAt: '2026-01-30 10:40:00',
  },
  {
    id: 2,
    name: '林婉清',
    alias: '娇蛮大小姐',
    gender: 'female',
    age: 20,
    personality: '娇蛮任性、视你为工具、内心矛盾',
    occupation: '林家二小姐',
    tags: ['豪门', '新婚', '真丝吊带'],
    intro: '新婚之夜让你睡地板，却在半夜穿着性感的真丝吊带在你面前晃荡。',
    background: '为了对抗父母逼婚，她选择了你这个“废物”入赘。',
    avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200',
    images: ['https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800'],
    videos: ['https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4'],
    scriptCount: 1,
    storyCount: 6,
    sceneCount: 6,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
  },
  {
    id: 3,
    name: '林雨欣',
    alias: '冰山总裁',
    gender: 'female',
    age: 22,
    personality: '高冷、强势、掌握家族命脉',
    occupation: '集团CEO',
    tags: ['御姐', '黑丝', '制服'],
    intro: '视你为吃软饭的废物，每次看你的眼神都像是在看垃圾。',
    background: '你的大姨子，紧绷的职业装下似乎隐藏着不为人知的压力。',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200',
    images: ['https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800'],
    videos: [],
    scriptCount: 1,
    storyCount: 6,
    sceneCount: 6,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
  },
  {
    id: 4,
    name: '苏曼柔',
    alias: '风韵岳母',
    gender: 'female',
    age: 38,
    personality: '威严、优雅、深不可测',
    occupation: '当家主母',
    tags: ['旗袍', '熟女', '深夜水声'],
    intro: '威严不可侵犯，时刻准备抓你的把柄，可深夜主卧传来的异样声响意味着什么？',
    background: '林家主母，风韵犹存，对你的入赘持有某种不可告人的目的。',
    avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200',
    images: ['https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800'],
    videos: [],
    scriptCount: 1,
    storyCount: 6,
    sceneCount: 6,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
  },
];

const parseLines = (value?: string) =>
  (value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const SocialRoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<RoleRecord[]>(initialRoles);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RoleRecord | null>(null);
  const [form] = Form.useForm();

  const exportTemplate = () => {
    const headers = [
      'name',
      'alias',
      'gender',
      'age',
      'personality',
      'occupation',
      'tags',
      'intro',
      'background',
      'avatar',
      'images',
      'videos',
      'scriptCount',
      'storyCount',
      'sceneCount',
      'status',
    ];
    const example = [
      '雨夜女王',
      '宋梨',
      'female',
      '25',
      '温柔、细腻、略带神秘',
      '舞蹈演员',
      '豪门|雨夜|禁忌',
      '雨夜老宅的神秘女主人，等待与你共度一晚。',
      '她背负旧案与家族秘密，外表冷静，内心渴望被理解。',
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200',
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800|https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800',
      'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4',
      '12',
      '28',
      '96',
      'active',
    ];
    const csv = `\ufeff${headers.join(',')}\n${example.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '女主角色导入模板.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseCsvLine = (line: string) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        const nextChar = line[i + 1];
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    result.push(current.trim());
    return result;
  };

  const parseCsv = async (file: File) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) {
      message.warning('导入文件为空或格式不正确');
      return;
    }
    const headers = parseCsvLine(lines[0]).map((h) => h.trim());
    const rows = lines.slice(1);
    const records: RoleRecord[] = rows.map((row, index) => {
      const columns = parseCsvLine(row);
      const map: Record<string, string> = {};
      headers.forEach((header, idx) => {
        map[header] = columns[idx] || '';
      });
      return {
        id: Date.now() + index,
        name: map.name || '未命名角色',
        alias: map.alias || '',
        gender: map.gender === 'male' ? 'male' : 'female',
        age: Number(map.age || 18),
        personality: map.personality || '',
        occupation: map.occupation || '',
        tags: map.tags ? map.tags.split('|').filter(Boolean) : [],
        intro: map.intro || '',
        background: map.background || '',
        avatar: map.avatar || '',
        images: map.images ? map.images.split('|').filter(Boolean) : [],
        videos: map.videos ? map.videos.split('|').filter(Boolean) : [],
        scriptCount: Number(map.scriptCount || 0),
        storyCount: Number(map.storyCount || 0),
        sceneCount: Number(map.sceneCount || 0),
        status: map.status === 'inactive' ? 'inactive' : 'active',
        updatedAt: new Date().toLocaleString('zh-CN'),
      };
    });
    setRoles((prev) => [...records, ...prev]);
    message.success(`成功导入 ${records.length} 条角色数据`);
    setImportModalVisible(false);
  };

  const filteredRoles = useMemo(() => {
    if (!searchText) return roles;
    const keyword = searchText.trim();
    return roles.filter((role) =>
      [role.name, role.alias, role.personality, role.occupation].some((field) => field.includes(keyword))
    );
  }, [roles, searchText]);

  const stats = useMemo(() => {
    const total = roles.length;
    const active = roles.filter((role) => role.status === 'active').length;
    const withVideo = roles.filter((role) => role.videos.length > 0).length;
    const mediaCount = roles.reduce((sum, role) => sum + role.images.length + role.videos.length, 0);
    return { total, active, withVideo, mediaCount };
  }, [roles]);

  const handleAdd = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEdit = (record: RoleRecord) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      images: record.images.join('\n'),
      videos: record.videos.join('\n'),
    });
    setEditModalVisible(true);
  };

  const handleSave = (values: any) => {
    const payload: RoleRecord = {
      id: selectedRecord?.id || Date.now(),
      name: values.name,
      alias: values.alias || '',
      gender: values.gender,
      age: values.age,
      personality: values.personality,
      occupation: values.occupation,
      tags: values.tags || [],
      intro: values.intro,
      background: values.background || '',
      avatar: values.avatar,
      images: parseLines(values.images),
      videos: parseLines(values.videos),
      scriptCount: Number(values.scriptCount || 0),
      storyCount: Number(values.storyCount || 0),
      sceneCount: Number(values.sceneCount || 0),
      status: values.status ? 'active' : 'inactive',
      updatedAt: new Date().toLocaleString('zh-CN'),
    };

    if (selectedRecord) {
      setRoles((prev) => prev.map((role) => (role.id === payload.id ? payload : role)));
      message.success('角色更新成功');
    } else {
      setRoles((prev) => [payload, ...prev]);
      message.success('角色创建成功');
    }
    setEditModalVisible(false);
    form.resetFields();
  };

  const handleDelete = (record: RoleRecord) => {
    setRoles((prev) => prev.filter((role) => role.id !== record.id));
    message.success(`已删除角色 ${record.name}`);
  };

  const handleView = (record: RoleRecord) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      render: (_: string, record: RoleRecord) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{record.name}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{record.alias || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '档案',
      key: 'profile',
      render: (_: any, record: RoleRecord) => (
        <Space direction="vertical" size={0}>
          <span>性别：{record.gender === 'female' ? '女' : '男'}</span>
          <span>年龄：{record.age}</span>
          <span>职业：{record.occupation}</span>
        </Space>
      ),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => (
        <Space wrap>
          {tags.map((tag) => (
            <Tag key={tag} color="magenta">
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '内容规模',
      key: 'volume',
      render: (_: any, record: RoleRecord) => (
        <Space direction="vertical" size={0}>
          <span>剧本：{record.scriptCount}</span>
          <span>故事：{record.storyCount}</span>
          <span>场景：{record.sceneCount}</span>
        </Space>
      ),
    },
    {
      title: '媒体素材',
      key: 'media',
      render: (_: any, record: RoleRecord) => (
        <Space>
          <Tag icon={<PictureOutlined />} color="blue">
            图片 {record.images.length}
          </Tag>
          <Tag icon={<VideoCameraOutlined />} color="purple">
            视频 {record.videos.length}
          </Tag>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: RoleRecord['status']) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: RoleRecord) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>
            查看
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该角色吗？" onConfirm={() => handleDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="女主角色管理"
        extra={
          <Space>
            <Input
              placeholder="搜索角色名称/别名/性格/职业"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => setSearchText('')}>
              重置
            </Button>
            <Button onClick={exportTemplate}>导出模板</Button>
            <Button onClick={() => setImportModalVisible(true)}>批量导入</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增角色
            </Button>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="角色总数" value={stats.total} />
          </Col>
          <Col span={6}>
            <Statistic title="启用角色" value={stats.active} />
          </Col>
          <Col span={6}>
            <Statistic title="含视频角色" value={stats.withVideo} />
          </Col>
          <Col span={6}>
            <Statistic title="素材总数" value={stats.mediaCount} />
          </Col>
        </Row>
        <Divider />
        <Table
          rowKey="id"
          dataSource={filteredRoles}
          columns={columns}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={selectedRecord ? '编辑角色' : '新增角色'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={760}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ status: true }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="角色名称" name="name" rules={[{ required: true, message: '请输入角色名称' }]}>
                <Input placeholder="如：雨夜女王" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="别名" name="alias">
                <Input placeholder="如：宋梨" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="性别" name="gender" rules={[{ required: true, message: '请选择性别' }]}>
                <Select placeholder="请选择">
                  <Option value="female">女</Option>
                  <Option value="male">男</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="年龄" name="age" rules={[{ required: true, message: '请输入年龄' }]}>
                <InputNumber min={18} max={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="职业" name="occupation" rules={[{ required: true, message: '请输入职业' }]}>
                <Input placeholder="舞蹈演员" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="性格" name="personality" rules={[{ required: true, message: '请输入性格' }]}>
            <Input placeholder="温柔/高冷/外向" />
          </Form.Item>
          <Form.Item label="标签" name="tags">
            <Select mode="tags" placeholder="输入后回车添加标签" />
          </Form.Item>
          <Form.Item label="角色简介" name="intro">
            <TextArea rows={2} placeholder="用于列表展示的简短介绍" />
          </Form.Item>
          <Form.Item label="身份背景" name="background">
            <TextArea rows={3} placeholder="用于详情页展示的角色背景" />
          </Form.Item>
          <Form.Item label="头像URL" name="avatar">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item label="图片素材URL（每行一个）" name="images">
            <TextArea rows={4} placeholder="https://...\nhttps://..." />
          </Form.Item>
          <Form.Item label="视频素材URL（每行一个）" name="videos">
            <TextArea rows={3} placeholder="https://...\nhttps://..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="剧本数量" name="scriptCount">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="故事数量" name="storyCount">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="场景数量" name="sceneCount">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="启用状态" name="status" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="角色详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={760}
      >
        {selectedRecord && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="角色档案">
              <Row gutter={16}>
                <Col span={12}>角色名称：{selectedRecord.name}</Col>
                <Col span={12}>别名：{selectedRecord.alias || '-'}</Col>
                <Col span={12}>性格：{selectedRecord.personality}</Col>
                <Col span={12}>性别：{selectedRecord.gender === 'female' ? '女' : '男'}</Col>
                <Col span={12}>年龄：{selectedRecord.age}</Col>
                <Col span={12}>职业：{selectedRecord.occupation}</Col>
                <Col span={12}>状态：{selectedRecord.status === 'active' ? '启用' : '停用'}</Col>
              </Row>
            </Card>
            <Card size="small" title="角色介绍">
              <p>{selectedRecord.intro || '-'}</p>
              <Divider style={{ margin: '12px 0' }} />
              <p>{selectedRecord.background || '-'}</p>
            </Card>
            <Card size="small" title="内容规模">
              <Row gutter={16}>
                <Col span={8}>剧本数量：{selectedRecord.scriptCount}</Col>
                <Col span={8}>故事数量：{selectedRecord.storyCount}</Col>
                <Col span={8}>场景数量：{selectedRecord.sceneCount}</Col>
              </Row>
            </Card>
            <Card size="small" title="素材信息">
              <p>头像：{selectedRecord.avatar || '-'}</p>
              <p>图片数量：{selectedRecord.images.length}</p>
              <p>视频数量：{selectedRecord.videos.length}</p>
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title="批量导入角色"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
      >
        <Alert
          type="info"
          showIcon
          message="上传CSV文件，字段用英文逗号分隔，列表字段用 | 连接。"
          description="必填字段：name, gender, age。status取值 active/inactive。"
          style={{ marginBottom: 16 }}
        />
        <Upload.Dragger
          name="file"
          accept=".csv"
          multiple={false}
          beforeUpload={(file) => {
            parseCsv(file);
            return false;
          }}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <PictureOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽CSV文件到此处导入</p>
          <p className="ant-upload-hint">建议先导出模板，按模板填写后上传</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default SocialRoleManagement;
